import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Stepper from '../../../../Stepper/Stepper'
import API from '../../../../../api/api'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import { ENTRY_NOTE_MAX_LENGTH } from '../../constants'
import ScheduledDateTimeFields from '../../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import { useScheduledDateTime } from '../../../ScheduledDateTimeFields/useScheduledDateTime'

const INITIAL_FORM_STATE = {
  title: '',
  description: '',
  pets: false,
  permissionToEnter: true,
  category: null,
  priority: null,
  entryNote: '',
}

const RentManagerSettings = ({
  building,
  resident,
  assignee,
  onBack,
  onCancel,
  voicemailChatMessageSid,
  inspectionId,
  description,
  checklistRoomItemId,
  onSuccess,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const scheduledDateTime = useScheduledDateTime({ timezone: building.timezone })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const [formState, setFormState] = useState({
    ...INITIAL_FORM_STATE,
    description: description || INITIAL_FORM_STATE.description,
  })
  const [rentManagerConfig, setRentManagerConfig] = useState({
    categories: [],
    priorities: [],
  })
  const [formErrors, setFormErrors] = useState({})

  const handleFieldChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleEntryNoteChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))

    // Validate entry note length
    if (value.length > ENTRY_NOTE_MAX_LENGTH) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: t('maintenance:form.pmsSettings.entryNote.validation', {
          maxLength: ENTRY_NOTE_MAX_LENGTH,
        }),
      }))
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormState((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!scheduledDateTime.validate()) return

    setIsSubmitting(true)
    const requestData = {
      buildingId: building.id,
      title: formState.title,
      description: formState.description,
      enterPermission: formState.permissionToEnter,
      pets: formState.pets,
      entryNote: formState.entryNote,
      unitId: resident.sourceUnitId,
      rentManager: true,
      ...(formState.category && { categoryId: formState.category.value }),
      ...(formState.priority && { priorityId: formState.priority.value }),
      ...(assignee && { assignee: assignee.id }),
      ...(scheduledDateTime.timestamp && { scheduledDate: scheduledDateTime.timestamp }),
    }

    if (resident.clientId) {
      requestData.clientId = resident.clientId
    }
    if (resident.sourceUnitId) {
      requestData.unitId = resident.sourceUnitId
    }

    if (voicemailChatMessageSid) {
      requestData.voicemailChatMessageSid = voicemailChatMessageSid
    }
    if (checklistRoomItemId) {
      requestData.checklistRoomItemId = checklistRoomItemId
    } else if (inspectionId) {
      requestData.inspectionId = inspectionId
    }

    try {
      await API.createMaintenance(requestData)
      displaySnackbar(t('maintenance:form.rentManager.snackbar.created'))
      if (onSuccess) {
        onSuccess()
      }
      onCancel()
    } catch {
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.error'), true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const areAllFieldsValid =
    !!formState.title &&
    !!formState.description &&
    !!formState.priority &&
    Object.keys(formErrors).length === 0

  const handleCategoryChange = (e) => {
    setFormState((prev) => ({ ...prev, category: e }))
    setFormState((prev) => ({ ...prev, priority: null }))
  }

  useEffect(() => {
    ;(async () => {
      const { data } = await API.getRentManagerConfig(building.id)

      const { categories: categoriesData = [], priorities: prioritiesData = [] } = data

      const categoriesOptions = categoriesData.map((category) => ({
        value: category.id,
        label: category.name,
      }))

      const prioritiesOptions = prioritiesData.map((priority) => ({
        value: priority.id,
        label: priority.name,
      }))

      setRentManagerConfig({
        categories: categoriesOptions,
        priorities: prioritiesOptions,
      })
    })()
  }, [building, building.id])

  return (
    <div className="maintenance-request-modal">
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={1}
      />
      <div className="row">
        <div className="row-header">{t('maintenance:form.rentManager.requestTitle')}</div>
        <TextField
          placeholder={t('maintenance:form.rentManager.requestTitle')}
          name="title"
          value={formState.title}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <ScheduledDateTimeFields {...scheduledDateTime.fieldProps} />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.requestDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.pmsSettings.descriptionPlaceholder')}
          name="description"
          value={formState.description}
          maxLength={250}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.rentManager.category')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={rentManagerConfig.categories}
          name="category"
          value={formState.category}
          onChange={handleCategoryChange}
          placeholder={t('maintenance:form.rentManager.categoryPlaceholder')}
        />
      </div>
      {formState.category && (
        <div className="row">
          <div className="row-header">{t('maintenance:form.rentManager.priority')}</div>
          <Select
            className="select-container"
            classNamePrefix="select-primary"
            options={rentManagerConfig.priorities}
            name="priority"
            value={formState.priority}
            onChange={(e) => setFormState((prev) => ({ ...prev, priority: e }))}
            placeholder={t('maintenance:form.rentManager.priorityPlaceholder')}
          />
        </div>
      )}
      <div className="row checkboxes">
        <div className="input-container">
          <ToggleField
            value={formState.pets}
            name="pets"
            label={t('maintenance:form.pmsSettings.pets')}
            labelOnRight
            onChange={handleToggleChange}
          />
        </div>
        <div className="input-container">
          <ToggleField
            name="permissionToEnter"
            label={t('maintenance:form.pmsSettings.permissionToEnter')}
            labelOnRight
            onChange={handleToggleChange}
            value={formState.permissionToEnter}
          />
        </div>
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.entryNote.title')}</div>
        <TextAreaField
          name="entryNote"
          placeholder={t('maintenance:form.pmsSettings.entryNote.placeholder')}
          value={formState.entryNote}
          onChange={handleEntryNoteChange}
          isError={!!formErrors.entryNote}
        />
        {formErrors.entryNote && <span className={'label--error'}>{formErrors.entryNote}</span>}
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray" onClick={onBack}>
            {t('maintenance:form.pmsSettings.back')}
          </button>
          <button className="button button-gray is-block" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary is-block"
            disabled={isSubmitting || !areAllFieldsValid}
            onClick={handleSubmit}
          >
            {t('maintenance:form.pmsSettings.createRequest')}
          </button>
        </div>
      </div>
    </div>
  )
}

RentManagerSettings.propTypes = {
  building: PropTypes.object,
  resident: PropTypes.object,
  assignee: PropTypes.object,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  voicemailChatMessageSid: PropTypes.bool,
  inspectionId: PropTypes.number,
  description: PropTypes.string,
  checklistRoomItemId: PropTypes.number,
  onSuccess: PropTypes.func,
}

export default RentManagerSettings
